import { saveUtilityCache } from '../../../utils/utilityAppStorage';
import { create } from 'zustand';
import {
  GROWTH_STORAGE_KEY,
  initialGrowthData,
  createRoute,
  createStage,
  createSkill,
  createProject,
  createStudyLogEntry,
  createWeeklyGoal,
  createMonthlyGoal,
  computeRouteProgress,
  computeStageProgress,
  computeStreak,
  getWeekStart,
  GROWTH_BACKUP_KEY,
} from '../utils/schema';
import {
  createLearningResource,
  createPlacement,
  migrateGrowthData,
  computeRouteEstimatedHours,
  stageHasResource,
  removeResourceFromRoutes,
  mergeGrowthPlannerData,
  recoverGrowthDataFromBackups,
  countRouteItems,
} from '../utils/learningResources';

const persistData = (data, { skipBackup = false } = {}) => {
  try {
    if (!skipBackup) {
      const existing = localStorage.getItem(GROWTH_STORAGE_KEY);
      if (existing) {
        localStorage.setItem(GROWTH_BACKUP_KEY, existing);
      }
    }
    const { selectedRouteId, changed, ...toSave } = data;
    saveUtilityCache(GROWTH_STORAGE_KEY, toSave);
  } catch (e) {
    console.error('Error saving growth planner data:', e);
  }
};

const loadData = () => {
  try {
    const stored = localStorage.getItem(GROWTH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      let base = { ...initialGrowthData, ...parsed };

      const itemCount = countRouteItems(base.routes);
      if (itemCount === 0) {
        base = recoverGrowthDataFromBackups(base);
      }

      const result = migrateGrowthData(base);
      const { changed, ...data } = result;
      if (changed || countRouteItems(data.routes) > itemCount) {
        persistData(data);
      }
      return data;
    }
  } catch (e) {
    console.error('Error loading growth planner data:', e);
  }
  const { changed, ...data } = migrateGrowthData({ ...initialGrowthData });
  return data;
};

const persist = (state) => {
  const { selectedRouteId, changed, ...toSave } = state;
  persistData(toSave);
};

const recalcRoute = (route, learningResources) => ({
  ...route,
  progress: computeRouteProgress(route, learningResources),
  estimatedHours: computeRouteEstimatedHours(route, learningResources),
  investedHours: route.stages?.reduce(
    (sum, stage) => sum + (stage.items?.reduce(
      (s, item) => s + (item.investedHours || 0), 0
    ) || 0), 0
  ) || route.investedHours || 0,
});

const recalcStage = (stage, learningResources) => ({
  ...stage,
  progress: computeStageProgress(stage, learningResources),
});

const updateRoutes = (state, updater) => {
  const routes = updater(state.routes, state.learningResources);
  const next = { ...state, routes };
  persist(next);
  return next;
};

export const useGrowthPlannerStore = create((set, get) => {
  const initial = loadData();

  return {
    ...initial,
    selectedRouteId: null,

    // ── Routes ──
    addRoute: (data) => set((s) => {
      const routeData = { ...data };
      const route = recalcRoute(createRoute(routeData), s.learningResources);
      if (route.routeKind === 'fast_track' && (!route.stages || route.stages.length === 0)) {
        route.stages = [createStage({ title: 'Ruta Rápida', description: 'Certificaciones prioritarias' })];
      }
      const next = { ...s, routes: [...s.routes, route] };
      persist(next);
      return next;
    }),

    updateRoute: (routeId, updates) => set((s) => {
      const routes = s.routes.map((r) =>
        r.id === routeId ? recalcRoute({ ...r, ...updates }, s.learningResources) : r
      );
      const next = { ...s, routes };
      persist(next);
      return next;
    }),

    deleteRoute: (routeId) => set((s) => {
      const next = {
        ...s,
        routes: s.routes.filter((r) => r.id !== routeId),
        selectedRouteId: s.selectedRouteId === routeId ? null : s.selectedRouteId,
      };
      persist(next);
      return next;
    }),

    setSelectedRoute: (routeId) => set({ selectedRouteId: routeId }),

    // ── Stages ──
    addStage: (routeId, data) => set((s) => updateRoutes(s, (routes, resources) =>
      routes.map((r) => {
        if (r.id !== routeId) return r;
        return recalcRoute({ ...r, stages: [...(r.stages || []), createStage(data)] }, resources);
      })
    )),

    updateStage: (routeId, stageId, updates) => set((s) => updateRoutes(s, (routes, resources) =>
      routes.map((r) => {
        if (r.id !== routeId) return r;
        const stages = (r.stages || []).map((st) =>
          st.id === stageId ? recalcStage({ ...st, ...updates }, resources) : st
        );
        return recalcRoute({ ...r, stages }, resources);
      })
    )),

    deleteStage: (routeId, stageId) => set((s) => updateRoutes(s, (routes, resources) =>
      routes.map((r) => {
        if (r.id !== routeId) return r;
        return recalcRoute({ ...r, stages: (r.stages || []).filter((st) => st.id !== stageId) }, resources);
      })
    )),

    reorderStages: (routeId, sourceIndex, destIndex) => set((s) => updateRoutes(s, (routes, resources) =>
      routes.map((r) => {
        if (r.id !== routeId) return r;
        const stages = Array.from(r.stages || []);
        const [removed] = stages.splice(sourceIndex, 1);
        stages.splice(destIndex, 0, removed);
        return recalcRoute({ ...r, stages }, resources);
      })
    )),

    reorderPlacements: (routeId, stageId, sourceIndex, destIndex) => set((s) => updateRoutes(s, (routes, resources) =>
      routes.map((r) => {
        if (r.id !== routeId) return r;
        const stages = (r.stages || []).map((st) => {
          if (st.id !== stageId) return st;
          const items = Array.from(st.items || []);
          const [removed] = items.splice(sourceIndex, 1);
          items.splice(destIndex, 0, removed);
          return recalcStage({ ...st, items }, resources);
        });
        return recalcRoute({ ...r, stages }, resources);
      })
    )),

    movePlacement: (routeId, fromStageId, toStageId, placementId, destIndex = null) => set((s) => {
      let moved = null;
      const routes = s.routes.map((r) => {
        if (r.id !== routeId) return r;
        let stages = (r.stages || []).map((st) => {
          if (st.id !== fromStageId) return st;
          const items = [...(st.items || [])];
          const idx = items.findIndex((p) => p.id === placementId);
          if (idx === -1) return st;
          [moved] = items.splice(idx, 1);
          return recalcStage({ ...st, items }, s.learningResources);
        });

        if (!moved) return r;

        if (stageHasResource(stages.find((st) => st.id === toStageId), moved.resourceId)) {
          stages = stages.map((st) => {
            if (st.id !== fromStageId) return st;
            return recalcStage({ ...st, items: [...(st.items || []), moved] }, s.learningResources);
          });
          return recalcRoute({ ...r, stages }, s.learningResources);
        }

        stages = stages.map((st) => {
          if (st.id !== toStageId) return st;
          const items = [...(st.items || [])];
          const insertAt = destIndex == null ? items.length : destIndex;
          items.splice(insertAt, 0, moved);
          return recalcStage({ ...st, items }, s.learningResources);
        });

        return recalcRoute({ ...r, stages }, s.learningResources);
      });

      const next = { ...s, routes };
      persist(next);
      return next;
    }),

    reorderRoutes: (sourceIndex, destIndex) => set((s) => {
      const routes = Array.from(s.routes);
      const [removed] = routes.splice(sourceIndex, 1);
      routes.splice(destIndex, 0, removed);
      const next = { ...s, routes };
      persist(next);
      return next;
    }),

    // ── Learning resources (catalog) ──
    createResource: (data) => {
      let resourceId = null;
      set((s) => {
        const resource = createLearningResource(data);
        resourceId = resource.id;
        const next = { ...s, learningResources: [...s.learningResources, resource] };
        persist(next);
        return next;
      });
      return resourceId;
    },

    updateResource: (resourceId, updates) => set((s) => {
      const next = {
        ...s,
        learningResources: s.learningResources.map((r) =>
          r.id === resourceId ? { ...r, ...updates } : r
        ),
      };
      const routes = next.routes.map((r) => recalcRoute(r, next.learningResources));
      const withRoutes = { ...next, routes };
      persist(withRoutes);
      return withRoutes;
    }),

    deleteResource: (resourceId) => set((s) => {
      const next = {
        ...s,
        learningResources: s.learningResources.filter((r) => r.id !== resourceId),
        routes: removeResourceFromRoutes(s.routes, resourceId).map((r) =>
          recalcRoute(r, s.learningResources.filter((res) => res.id !== resourceId))
        ),
      };
      persist(next);
      return next;
    }),

    addPlacement: (routeId, stageId, { resourceId, targetDate = '' }) => set((s) => {
      const stage = s.routes.find((r) => r.id === routeId)?.stages?.find((st) => st.id === stageId);
      if (stage && stageHasResource(stage, resourceId)) {
        return s;
      }

      return updateRoutes(s, (routes, resources) =>
        routes.map((r) => {
          if (r.id !== routeId) return r;
          const stages = (r.stages || []).map((st) => {
            if (st.id !== stageId) return st;
            const placement = createPlacement(resourceId, { targetDate });
            return recalcStage({ ...st, items: [...(st.items || []), placement] }, resources);
          });
          return recalcRoute({ ...r, stages }, resources);
        })
      );
    }),

    addItem: (routeId, stageId, data) => set((s) => {
      if (data.resourceId) {
        const stage = s.routes.find((r) => r.id === routeId)?.stages?.find((st) => st.id === stageId);
        if (stage && stageHasResource(stage, data.resourceId)) return s;

        return updateRoutes(s, (routes, resources) =>
          routes.map((r) => {
            if (r.id !== routeId) return r;
            const stages = (r.stages || []).map((st) => {
              if (st.id !== stageId) return st;
              const placement = createPlacement(data.resourceId, { targetDate: data.targetDate || '' });
              return recalcStage({ ...st, items: [...(st.items || []), placement] }, resources);
            });
            return recalcRoute({ ...r, stages }, resources);
          })
        );
      }

      const resource = createLearningResource({
        type: data.type,
        title: data.title,
        institution: data.institution,
        url: data.url,
        estimatedHours: data.estimatedHours,
        status: data.status,
        progress: data.progress,
        startDate: data.startDate,
        notes: data.notes,
        skills: data.skills,
      });

      const learningResources = [...s.learningResources, resource];
      const routes = s.routes.map((r) => {
        if (r.id !== routeId) return r;
        const stages = (r.stages || []).map((st) => {
          if (st.id !== stageId) return st;
          const placement = createPlacement(resource.id, { targetDate: data.targetDate || '' });
          const items = [...(st.items || []), placement];
          return recalcStage({ ...st, items }, learningResources);
        });
        return recalcRoute({ ...r, stages }, learningResources);
      });

      const next = { ...s, learningResources, routes };
      persist(next);
      return next;
    }),

    updatePlacement: (routeId, stageId, placementId, updates) => set((s) => {
      const { targetDate, ...resourceUpdates } = updates;
      let learningResources = s.learningResources;
      const placement = s.routes
        .flatMap((r) => r.stages || [])
        .flatMap((st) => st.items || [])
        .find((p) => p.id === placementId);

      if (placement?.resourceId && Object.keys(resourceUpdates).length > 0) {
        learningResources = learningResources.map((r) =>
          r.id === placement.resourceId ? { ...r, ...resourceUpdates } : r
        );
      }

      const routes = s.routes.map((r) => {
        if (r.id !== routeId) return r;
        const stages = (r.stages || []).map((st) => {
          if (st.id !== stageId) return st;
          const items = (st.items || []).map((p) =>
            p.id === placementId
              ? { ...p, ...(targetDate !== undefined ? { targetDate } : {}) }
              : p
          );
          return recalcStage({ ...st, items }, learningResources);
        });
        return recalcRoute({ ...r, stages }, learningResources);
      });

      const next = { ...s, learningResources, routes };
      persist(next);
      return next;
    }),

    updateItem: (routeId, stageId, placementId, updates) => {
      get().updatePlacement(routeId, stageId, placementId, updates);
    },

    updateResourceByPlacement: (routeId, stageId, placementId, resourceUpdates) => set((s) => {
      const placement = (s.routes.find((r) => r.id === routeId)?.stages || [])
        .find((st) => st.id === stageId)?.items?.find((p) => p.id === placementId);
      if (!placement?.resourceId) return s;

      const learningResources = s.learningResources.map((r) =>
        r.id === placement.resourceId ? { ...r, ...resourceUpdates } : r
      );
      const routes = s.routes.map((r) => recalcRoute(r, learningResources));
      const next = { ...s, learningResources, routes };
      persist(next);
      return next;
    }),

    removePlacement: (routeId, stageId, placementId) => set((s) => updateRoutes(s, (routes, resources) =>
      routes.map((r) => {
        if (r.id !== routeId) return r;
        const stages = (r.stages || []).map((st) => {
          if (st.id !== stageId) return st;
          return recalcStage({
            ...st,
            items: (st.items || []).filter((p) => p.id !== placementId),
          }, resources);
        });
        return recalcRoute({ ...r, stages }, resources);
      })
    )),

    deleteItem: (routeId, stageId, placementId) => {
      get().removePlacement(routeId, stageId, placementId);
    },

    reorderItems: (routeId, stageId, sourceIndex, destIndex) => {
      get().reorderPlacements(routeId, stageId, sourceIndex, destIndex);
    },

    // ── Skills ──
    addSkill: (data) => set((s) => {
      const next = { ...s, skills: [...s.skills, createSkill(data)] };
      persist(next);
      return next;
    }),

    updateSkill: (skillId, updates) => set((s) => {
      const next = { ...s, skills: s.skills.map((sk) => sk.id === skillId ? { ...sk, ...updates } : sk) };
      persist(next);
      return next;
    }),

    deleteSkill: (skillId) => set((s) => {
      const next = { ...s, skills: s.skills.filter((sk) => sk.id !== skillId) };
      persist(next);
      return next;
    }),

    importSkills: (skillList) => set((s) => {
      const existing = new Set(s.skills.map((sk) => sk.name.toLowerCase()));
      const newSkills = skillList
        .filter((sk) => sk.name && !existing.has(sk.name.toLowerCase()))
        .map((sk) => createSkill({ name: sk.name }));
      const next = { ...s, skills: [...s.skills, ...newSkills] };
      persist(next);
      return next;
    }),

    // ── Projects ──
    addProject: (data) => set((s) => {
      const next = { ...s, projects: [...s.projects, createProject(data)] };
      persist(next);
      return next;
    }),

    updateProject: (projectId, updates) => set((s) => {
      const next = {
        ...s,
        projects: s.projects.map((p) => p.id === projectId ? { ...p, ...updates } : p),
      };
      persist(next);
      return next;
    }),

    deleteProject: (projectId) => set((s) => {
      const next = { ...s, projects: s.projects.filter((p) => p.id !== projectId) };
      persist(next);
      return next;
    }),

    linkProjectToRoute: (projectId, routeId) => set((s) => {
      const projects = s.projects.map((p) => {
        if (p.id !== projectId) return p;
        const routeIds = p.routeIds.includes(routeId) ? p.routeIds : [...p.routeIds, routeId];
        return { ...p, routeIds };
      });
      const routes = s.routes.map((r) => {
        if (r.id !== routeId) return r;
        const projectIds = r.projectIds.includes(projectId) ? r.projectIds : [...r.projectIds, projectId];
        return { ...r, projectIds };
      });
      const next = { ...s, projects, routes };
      persist(next);
      return next;
    }),

    // ── Focus / Study ──
    logStudySession: (entry) => set((s) => {
      const logEntry = createStudyLogEntry(entry);
      const studyLog = [...s.focus.studyLog, logEntry];
      const streak = computeStreak(studyLog);

      const weekStart = getWeekStart();
      const weeklyGoals = s.focus.weeklyGoals.map((g) =>
        g.weekStart === weekStart
          ? { ...g, completedHours: (g.completedHours || 0) + (entry.hours || 0) }
          : g
      );

      const month = new Date().toISOString().slice(0, 7);
      const monthlyGoals = s.focus.monthlyGoals.map((g) =>
        g.month === month
          ? { ...g, completedHours: (g.completedHours || 0) + (entry.hours || 0) }
          : g
      );

      const next = {
        ...s,
        focus: { ...s.focus, studyLog, streak, weeklyGoals, monthlyGoals },
      };
      persist(next);
      return next;
    }),

    addWeeklyGoal: (data) => set((s) => {
      const next = {
        ...s,
        focus: {
          ...s.focus,
          weeklyGoals: [...s.focus.weeklyGoals, createWeeklyGoal(data)],
        },
      };
      persist(next);
      return next;
    }),

    addMonthlyGoal: (data) => set((s) => {
      const next = {
        ...s,
        focus: {
          ...s.focus,
          monthlyGoals: [...s.focus.monthlyGoals, createMonthlyGoal(data)],
        },
      };
      persist(next);
      return next;
    }),

    deleteWeeklyGoal: (goalId) => set((s) => {
      const next = {
        ...s,
        focus: { ...s.focus, weeklyGoals: s.focus.weeklyGoals.filter((g) => g.id !== goalId) },
      };
      persist(next);
      return next;
    }),

    deleteMonthlyGoal: (goalId) => set((s) => {
      const next = {
        ...s,
        focus: { ...s.focus, monthlyGoals: s.focus.monthlyGoals.filter((g) => g.id !== goalId) },
      };
      persist(next);
      return next;
    }),

    getRoute: (routeId) => get().routes.find((r) => r.id === routeId),

    reconcileLibrary: () => set((s) => {
      const { changed, ...migrated } = migrateGrowthData({
        ...initialGrowthData,
        routes: s.routes,
        learningResources: s.learningResources,
        skills: s.skills,
        projects: s.projects,
        focus: s.focus,
        migrationReport: s.migrationReport,
        _migratedResources: s._migratedResources,
        _migrationVersion: s._migrationVersion,
      });
      const routes = migrated.routes.map((r) => recalcRoute(r, migrated.learningResources));
      const next = { ...s, ...migrated, routes };
      persist(next);
      return next;
    }),

    dismissMigrationReport: () => set((s) => {
      const next = {
        ...s,
        migrationReport: { ...s.migrationReport, dismissed: true },
      };
      persist(next);
      return next;
    }),

    importGrowthData: (rawJson) => {
      let incoming;
      try {
        incoming = typeof rawJson === 'string' ? JSON.parse(rawJson) : rawJson;
      } catch {
        return { ok: false, error: 'JSON inválido' };
      }

      let result = { ok: true, items: 0, mergedGroups: 0 };
      set((s) => {
        const merged = mergeGrowthPlannerData(
          {
            routes: s.routes,
            learningResources: s.learningResources,
            skills: s.skills,
            projects: s.projects,
            focus: s.focus,
            migrationReport: s.migrationReport,
          },
          incoming
        );
        const { changed, ...migrated } = merged;
        const routes = migrated.routes.map((r) => recalcRoute(r, migrated.learningResources));
        const next = { ...s, ...migrated, routes };
        persist(next);
        result = {
          ok: true,
          items: countRouteItems(routes),
          mergedGroups: migrated.migrationReport?.mergedGroups?.length || 0,
        };
        return next;
      });
      return result;
    },

    recoverFromBackups: () => set((s) => {
      const recovered = recoverGrowthDataFromBackups({
        routes: s.routes,
        learningResources: s.learningResources,
        skills: s.skills,
        projects: s.projects,
        focus: s.focus,
      });
      const { changed, ...migrated } = migrateGrowthData(recovered);
      const routes = migrated.routes.map((r) => recalcRoute(r, migrated.learningResources));
      const next = {
        ...s,
        ...migrated,
        routes,
        migrationReport: {
          ...(migrated.migrationReport || {}),
          dismissed: false,
          recoveredAt: new Date().toISOString(),
        },
      };
      persist(next);
      return next;
    }),

    rehydrate: () => set(() => {
      const data = loadData();
      const routes = data.routes.map((r) => recalcRoute(r, data.learningResources));
      return { ...data, routes, selectedRouteId: null };
    }),
  };
});
